import { LoadingButton } from '@mui/lab';
import { Alert, Button, Card, CardActions, DialogContentText, Snackbar, Stack, SxProps } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { isBefore } from 'date-fns';
import React, { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DetailsAccordion, DetailsCard } from '../assignment-details-accordion/AssignmentDetailsAccordion';
import ErrorDialog from '../error-dialog/ErrorDialog';
import AttemptsField from '../forms/attempts-field/AttemptsField';
import DescriptionField from '../forms/description-field/DescriptionField';
import EndTimeField from '../forms/end-time-field/EndTimeField';
import StartTimeField from '../forms/start-time-field/StartTimeField';
import TimezoneField from '../forms/timezone-field/TimezoneField';
import VisibilityField from '../forms/visibility-field/VisibilityField';
import { ExerciseType, Timezone } from '../types';
import { useDesktop } from '../useDesktop';
import { useSaveExercise } from './useSaveExercise';

export interface EditAssignmentDetailsProps {
  description: string;
  endTime?: Date;
  exercise: {
    adHoc: boolean;
  };
  id: string;
  maxNumberOfAttempts?: number;
  startTime: Date;
  sx?: SxProps;
  timeZone: string;
  type: ExerciseType;
  visibility: boolean;
}

export interface EditExerciseFormData {
  description: string;
  endTime: Date | null;
  limitNumberOfAttempts: boolean;
  maxNumberOfAttempts: string;
  startTime: Date;
  timeRange: string; // Only for errors
  timezone: string;
  visibility: boolean;
}

const EditAssignmentDetails = ({
  description,
  endTime,
  exercise: { adHoc },
  id,
  maxNumberOfAttempts,
  startTime,
  sx = {},
  timeZone,
  type,
  visibility,
}: EditAssignmentDetailsProps) => {
  const isDesktop = useDesktop();

  const Wrapper = isDesktop ? DetailsCard : DetailsAccordion;

  const { data: timezones } = useQuery<Timezone[]>({ queryKey: [{ path: 'time-zones' }] });
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const { clearErrors, control, formState, getValues, handleSubmit, reset, setError, trigger, watch } =
    useForm<EditExerciseFormData>({
      defaultValues: {
        description,
        endTime: endTime,
        limitNumberOfAttempts: !!maxNumberOfAttempts,
        maxNumberOfAttempts: maxNumberOfAttempts?.toString() || '',
        startTime: startTime,
        timezone: timeZone,
        visibility,
        timeRange: '',
      },
    });

  // Revalidate start/end time when start/end time or timezone changes
  const [timezoneValue, startTimeValue, endTimeValue] = watch(['timezone', 'startTime', 'endTime']);
  useLayoutEffect(() => {
    trigger(['startTime', 'endTime']);
    if (startTimeValue && endTimeValue && isBefore(endTimeValue, startTimeValue)) {
      setError('timeRange', { message: 'Start time should be before end time' });
    } else {
      clearErrors('timeRange');
    }
  }, [timezoneValue, startTimeValue, endTimeValue]);

  const selectedTimezone = timezones?.find(tz => tz.id === timeZone);

  const { savingExercise, errorDialog, saveExercise, setErrorDialog } = useSaveExercise(id, setError, () => {
    setShowSuccessToast(true);
    reset(getValues()); // set form pristine
  });

  const onSubmit = (data, e) => {
    e?.preventDefault();
    saveExercise(data);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
        <Card component="aside" sx={{ backgroundColor: theme => theme.palette.background.default, ...sx }}>
          <Wrapper
            actions={
              <CardActions>
                <Button disabled={savingExercise} size="small" onClick={() => reset()} sx={{ ml: 'auto' }}>
                  Reset
                </Button>
                <LoadingButton
                  disabled={!formState.isDirty}
                  loading={savingExercise}
                  size="small"
                  type="submit"
                  variant="contained"
                >
                  Save
                </LoadingButton>
              </CardActions>
            }
            header="Assignment details"
          >
            <Stack gap={2}>
              <DescriptionField control={control} name="description" />
              {!adHoc && <TimezoneField control={control} name="timezone" timezones={timezones ?? []} />}
              <StartTimeField control={control} disabled={adHoc} name="startTime" />
              {!adHoc && <EndTimeField control={control} name="endTime" timezone={selectedTimezone} />}
              {type === ExerciseType.SelfStudy && <AttemptsField control={control} name="maxNumberOfAttempts" />}
              <VisibilityField control={control} name="visibility" />
            </Stack>
          </Wrapper>
        </Card>
      </form>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        open={showSuccessToast}
      >
        <Alert onClose={() => setShowSuccessToast(false)} severity="success">
          Exercise saved successfully
        </Alert>
      </Snackbar>

      <ErrorDialog
        open={errorDialog.open}
        message={
          <DialogContentText color="text.primary" variant="body1">
            {errorDialog.message}
          </DialogContentText>
        }
      >
        <Button variant="contained" onClick={() => setErrorDialog({ ...errorDialog, open: false })}>
          Dismiss
        </Button>
      </ErrorDialog>
    </>
  );
};

export default EditAssignmentDetails;
